import useStore from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { ICanvasNodesResponse } from '@store/services/nodes/types';
import { initCollapsedNodes } from '@utils/canvasHelpers';
import {
  formatEdgesForCanvas,
  formatNodeDTOToCanvasNode,
} from '@utils/helpers';
import { Edge, Node } from 'reactflow';
import { Subject } from 'rxjs';

export const saveEntitiesSubject = new Subject<{
  nodes: Node[];
  edges: Edge[];
  oldNodes: Node[];
}>();

const getPersistedCanvasNodes = () =>
  useStore.getState().nodes.filter(({ id }) => !id.startsWith('local'));

const useSaveEntitiesResponse = () => {
  const { setNodes, setEdges } = useStore();

  const applyToStore = (
    newCanvasNodes: Node[],
    newCanvasEdges: Edge[],
    oldCanvasNodes: Node[],
  ) => {
    setNodes(newCanvasNodes);
    setEdges(newCanvasEdges);

    saveEntitiesSubject.next({
      nodes: newCanvasNodes,
      edges: newCanvasEdges,
      oldNodes: oldCanvasNodes,
    });

    return { nodes: newCanvasNodes, relations: newCanvasEdges };
  };

  const saveResponseToRedux = ({ nodes, relations }: ICanvasNodesResponse) => {
    const oldCanvasNodes = getPersistedCanvasNodes();

    const canvasNodes = nodes.map(formatNodeDTOToCanvasNode);

    const newCanvasNodes = [
      ...oldCanvasNodes.filter(({ type }) =>
        [NodesTypes.LayerNode, NodesTypes.SubLayerNode].includes(
          type as NodesTypes,
        ),
      ),

      ...initCollapsedNodes(canvasNodes),
    ];

    const newCanvasEdges = formatEdgesForCanvas(relations);

    return applyToStore(newCanvasNodes, newCanvasEdges, oldCanvasNodes);
  };

  /** Model Evolution: patch only the saved node; keep insight state (hidden, diff, canvas filters). */
  const saveModelEvolutionResponseToRedux = (
    response: ICanvasNodesResponse,
    updatedNodeId: string,
  ) => {
    const oldCanvasNodes = getPersistedCanvasNodes();
    const updatedDto = response.nodes.find((n) => n.id === updatedNodeId);

    if (!updatedDto) {
      return saveResponseToRedux(response);
    }

    const layerNodes = oldCanvasNodes.filter(({ type }) =>
      [NodesTypes.LayerNode, NodesTypes.SubLayerNode].includes(
        type as NodesTypes,
      ),
    );

    const customNodes = oldCanvasNodes.filter(
      ({ type }) => type === NodesTypes.CustomNode,
    );

    const mergedCustom = customNodes.map((node) => {
      if (node.id !== updatedNodeId) return node;

      const fresh = formatNodeDTOToCanvasNode(updatedDto);

      return {
        ...node,
        position: fresh.position,
        parentNode: fresh.parentNode,
        extent: fresh.extent,
        type: fresh.type,
        hidden: node.hidden,
        data: {
          ...node.data,
          dto: fresh.data.dto,
        },
      };
    });

    const newCanvasNodes = [...layerNodes, ...initCollapsedNodes(mergedCustom)];
    const newCanvasEdges = formatEdgesForCanvas(response.relations);

    return applyToStore(newCanvasNodes, newCanvasEdges, oldCanvasNodes);
  };

  return { saveResponseToRedux, saveModelEvolutionResponseToRedux };
};

export default useSaveEntitiesResponse;
